import { useCallback, useEffect, useState } from 'react';
import type { Area, Point } from 'react-easy-crop';
import Cropper from 'react-easy-crop';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Slider } from '~shared/shadcn/ui/slider';

interface ImageCropperDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  onCropComplete: (croppedFile: File, croppedImageUrl: string) => void;
  aspectRatio?: number;
  outputWidth?: number;
  outputHeight?: number;
  title?: string;
  description?: string;
  minZoom?: number;
  maxZoom?: number;
}

/**
 * Creates a cropped image file from the crop area
 */
async function createCroppedImage(
  imageSrc: string,
  pixelCrop: Area,
  outputWidth?: number,
  outputHeight?: number,
): Promise<{ file: File; url: string }> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Use output dimensions if provided, otherwise use crop area dimensions
  const targetWidth = outputWidth ?? pixelCrop.width;
  const targetHeight = outputHeight ?? pixelCrop.height;

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    targetWidth,
    targetHeight,
  );

  return new Promise((resolve, reject) => {
    // Determine file type from imageSrc (data URL or regular URL)
    let fileType = 'image/png';
    let fileExtension = 'png';

    if (imageSrc.startsWith('data:')) {
      const mimeMatch = /data:([^;]+)/.exec(imageSrc);
      if (mimeMatch?.[1]) {
        fileType = mimeMatch[1];
        fileExtension =
          fileType.includes('jpeg') || fileType.includes('jpg') ? 'jpg' : 'png';
      }
    }

    // Use appropriate MIME type for toBlob
    const blobMimeType =
      fileType === 'image/jpeg' || fileType === 'image/jpg'
        ? 'image/jpeg'
        : 'image/png';

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }

        const fileName = `cropped-${Date.now()}.${fileExtension}`;

        const file = new File([blob], fileName, {
          type: fileType,
        });
        const url = URL.createObjectURL(blob);
        resolve({ file, url });
      },
      blobMimeType,
      1.0,
    );
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => {
      resolve(image);
    });
    image.addEventListener('error', (error) => {
      reject(new Error(`Failed to load image: ${error.message}`));
    });
    image.src = url;
  });
}

export function ImageCropperDialog({
  open,
  onOpenChange,
  imageSrc,
  onCropComplete,
  aspectRatio,
  outputWidth,
  outputHeight,
  title = 'Crop Image',
  description = 'Adjust the image to your desired crop area',
  minZoom = 0.5,
  maxZoom = 5,
}: ImageCropperDialogProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const resetState = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }, []);

  const onCropChange = useCallback((crop: Point) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setIsProcessing(true);
    try {
      const { file, url } = await createCroppedImage(
        imageSrc,
        croppedAreaPixels,
        outputWidth,
        outputHeight,
      );
      onCropComplete(file, url);
      onOpenChange(false);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [
    croppedAreaPixels,
    imageSrc,
    outputWidth,
    outputHeight,
    onCropComplete,
    onOpenChange,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] w-[95vw] max-w-5xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className='relative h-[400px] w-full bg-black sm:h-[500px] md:h-[600px] lg:h-[700px]'>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            minZoom={minZoom}
            maxZoom={maxZoom}
            restrictPosition={false}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
              },
            }}
          />
        </div>

        <div className='space-y-4 px-2'>
          <div className='space-y-2'>
            <label className='text-sm font-medium'>Zoom</label>
            <Slider
              value={[zoom]}
              min={minZoom}
              max={maxZoom}
              step={0.1}
              onValueChange={(value) => {
                setZoom(value[0] ?? 1);
              }}
              className='w-full'
            />
          </div>
          {outputWidth && outputHeight && (
            <p className='text-xs text-muted-foreground'>
              Output size: {outputWidth} × {outputHeight}px
            </p>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline' disabled={isProcessing}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleSave}
            disabled={isProcessing || !croppedAreaPixels}
          >
            {isProcessing ? 'Processing...' : 'Save Crop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
